import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { range } from 'react-select-datepicker/dist/utils/helpers';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { v4 as uuid } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { UPDATE_ACCOUNT } from 'store/settings';
import { settingsActions } from 'store';
import { useFindLoad } from 'hooks/useFindLoad';
import getYear from 'date-fns/getYear';
import getMonth from 'date-fns/getMonth';
import { LoadingButton } from 'components';
import { months } from 'utils/constant';
import { GenderSuggestionData } from 'containers/settings/PasswordEdit/data';
import { settingsPageScheme } from 'validationSchemes/settingsPageScheme';
import { StyledErrorMessage } from 'containers/Contact/fragments/InputSection/styled';
import { Images } from 'assets/images';
import { ROUTES } from 'utils/routeNames';
import {
  EditUserInfo,
  FormButton,
  SettingsContainer,
  GenderPickBlock,
  GenderSuggestions,
  GenderText,
  InputBlock,
  PasswordVisibleIcon,
  StyledInput,
  BackSubmitBlock,
  SelectOption,
  SelectBlock,
  StyledOption,
  RadioCheckbox,
  CheckboxText,
  ButtonFormContainer,
  StyledInputData,
  DatePickerBlock,
  SelectYear,
  SelectMonth,
  SampleContainer,
  StyledErrorContent,
  ErrorWrapper,
} from '../styled';

export const ProfileEdit = ({
  clonedData,
  setClonedData,
  isRemoveAvatar,
  isChangedImage,
  setIsChangedImage,
}) => {
  const { cities, countries, userAvatarPath } = useSelector(
    ({ settings }) => settings,
  );
  const [userBirthDate, setUserBirthDate] = useState();
  const [cityDefaultData, setCityDefaultData] = useState(clonedData.city);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoading = useFindLoad(UPDATE_ACCOUNT);
  const currentUserBirthdate = userBirthDate ? new Date(userBirthDate) : null;

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
    reset,
    watch,
    control,
  } = useForm({
    defaultValues: {
      name: clonedData?.name,
      gender: clonedData?.gender,
      country_id: '',
      city_id: '',
    },
    resolver: yupResolver(settingsPageScheme),
    mode: 'onChange',
  });

  const years = range(1910, getYear(new Date()) + 1, 1);

  const onSubmit = (data) => {
    const newDate = new Date(data.birthdate).toISOString().split('T')[0];

    setClonedData((prevState) => ({
      ...prevState,
      name: data.name,
      gender: data.gender,
      city_id: data?.city_id,
      country_id: data.country_id,
      birthdate: newDate,
      is_remove_avatar: 0,
    }));

    const defaults = {
      name: data.name,
      gender: data.gender,
      country_id: data.country_id,
    };

    data = {
      name: data.name,
      gender: data.gender,
      city_id: data?.city_id,
      country_id: data.country_id,
      birthdate: newDate,
      is_remove_avatar: 0,
    };

    if (
      data.country_id !== clonedData?.country?.id &&
      +data.city_id === clonedData.city?.id &&
      cities?.data?.[0]?.id
    ) {
      data.city_id = cities?.data?.[0]?.id;
    }

    reset(defaults);

    const mergeObjects = (dirtyFields, data) => {
      const userData = { ...dirtyFields };
      for (const key of Object.keys(data)) {
        if (key in userData) {
          userData[key] = data[key];
        }
      }

      return userData;
    };

    const resultData = mergeObjects(dirtyFields, data);

    if (clonedData.birthdate !== data.birthdate) {
      resultData.birthdate = data.birthdate;
    }

    if (!isRemoveAvatar && isChangedImage) {
      resultData.profile_photo_path =
        userAvatarPath ?? clonedData?.profile_photo_path;
      resultData.is_remove_avatar = 0;
    } else if (isRemoveAvatar === 1) {
      resultData.is_remove_avatar = 1;
    }

    setIsChangedImage(false);

    dispatch(settingsActions.updateAccount(resultData));
  };

  const handleCancel = () => {
    navigate(ROUTES.HOME);
  };

  useEffect(() => {
    const countryId = watch('country_id');

    if (countryId) {
      dispatch(settingsActions.getCities(countryId));
    }

    if (+countryId !== clonedData?.country?.id) {
      setCityDefaultData(cities?.data?.[0]);
    }
  }, [watch('country_id'), cities?.data?.[0]?.id]);

  useEffect(() => {
    setUserBirthDate(clonedData?.birthdate);
  }, [clonedData?.birthdate]);

  useEffect(() => {
    const defaults = {
      name: clonedData?.name,
      gender: clonedData?.gender,
      birthdate: currentUserBirthdate,
    };

    reset(defaults);
  }, [clonedData?.name, clonedData?.gender, userBirthDate]);

  useEffect(() => {
    setCityDefaultData(clonedData.city);
  }, [clonedData?.city?.id, clonedData?.country]);

  return (
    <EditUserInfo>
      <SettingsContainer onSubmit={handleSubmit(onSubmit)}>
        <SampleContainer>
          <InputBlock>
            <StyledInput
              placeholder="Full Name"
              type="text"
              {...register('name')}
              error={errors.name?.message}
            />
            <StyledErrorContent>{errors.name?.message}</StyledErrorContent>
          </InputBlock>
        </SampleContainer>

        <SampleContainer>
          <SelectOption>
            <SelectBlock
              {...register('country_id')}
              defaultColor={!!cityDefaultData?.id}
              errorColor={errors.country_id && true}
              className={errors.country_id ? 'active' : 'inactive'}
              error={errors.country_id?.message}
            >
              {clonedData?.country?.country_name ? (
                <StyledOption selected={true} value={clonedData?.country?.id}>
                  {clonedData?.country?.country_name}
                </StyledOption>
              ) : (
                <StyledOption value="">Country</StyledOption>
              )}
              {countries?.data?.map((item) => (
                <StyledOption key={item.id} value={item?.id}>
                  {item?.country_name}
                </StyledOption>
              ))}
            </SelectBlock>
            <PasswordVisibleIcon src={Images.downVector} />
          </SelectOption>
          <StyledErrorContent>{errors.country_id?.message}</StyledErrorContent>
        </SampleContainer>

        <InputBlock>
          <Controller
            control={control}
            name="birthdate"
            render={({ field }) => (
              <StyledInputData
                placeholderText="Date of Birth"
                {...register('birthdate')}
                selected={
                  field.value !== undefined ? field?.value : currentUserBirthdate
                }
                autoComplete="off"
                error={errors?.birthdate?.message}
                onChange={(e) => field.onChange(e)}
                onKeyDown={(e) => {
                  e.preventDefault();
                }}
                renderCustomHeader={({ date, changeYear, changeMonth }) => (
                  <DatePickerBlock>
                    <SelectYear
                      value={getYear(date)}
                      onChange={({ target: { value } }) => changeYear(value)}
                    >
                      {years.map((option) => (
                        <StyledOption key={uuid()} value={option}>
                          {option}
                        </StyledOption>
                      ))}
                    </SelectYear>
                    <SelectMonth
                      value={months[getMonth(date)]}
                      onChange={({ target: { value } }) =>
                        changeMonth(months.indexOf(value))
                      }
                    >
                      {months.map((option) => (
                        <StyledOption key={uuid()} value={option}>
                          {option}
                        </StyledOption>
                      ))}
                    </SelectMonth>
                  </DatePickerBlock>
                )}
              />
            )}
          />

          <ErrorWrapper>
            <StyledErrorMessage>{errors?.birthdate?.message}</StyledErrorMessage>
          </ErrorWrapper>
        </InputBlock>

        <SampleContainer>
          <SelectOption>
            <SelectBlock
              {...register('city_id')}
              className={errors.country_id ? 'active' : 'inactive'}
              defaultColor={!!cityDefaultData?.id}
              error={errors.city_id?.message}
            >
              {cityDefaultData?.id ? (
                <StyledOption value={cityDefaultData?.id} selected={true}>
                  {cityDefaultData?.name}
                </StyledOption>
              ) : (
                <StyledOption value="" placeholder>
                  City
                </StyledOption>
              )}

              {cities.data?.map((item) => (
                <StyledOption value={item?.id} key={item?.id}>
                  {item?.name}
                </StyledOption>
              ))}
            </SelectBlock>
            <PasswordVisibleIcon src={Images.downVector} />
          </SelectOption>
          <StyledErrorContent>{errors.city_id?.message}</StyledErrorContent>
        </SampleContainer>

        <GenderPickBlock>
          <GenderText>Gender</GenderText>
          <GenderSuggestions>
            {GenderSuggestionData.map((element) => (
              <CheckboxText
                key={uuid()}
                className={errors.gender ? 'active' : 'inactive'}
              >
                <RadioCheckbox
                  {...register('gender')}
                  type={element.type}
                  value={element.gender}
                  name={element.name}
                  error={errors.gender?.message}
                />
                {element.gender}
              </CheckboxText>
            ))}
          </GenderSuggestions>
        </GenderPickBlock>

        <BackSubmitBlock>
          <FormButton onClick={handleCancel}>Cancel</FormButton>
          <ButtonFormContainer>
            <LoadingButton loading={isLoading} name="Save Changes" />
          </ButtonFormContainer>
        </BackSubmitBlock>
      </SettingsContainer>
    </EditUserInfo>
  );
};
